import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  Cell,
} from "recharts";
import { Ruler, TrendingUp, Ship, MapPinned } from "lucide-react";
import {
  DashboardShell,
  PageHeading,
  ChartCard,
  tooltipStyle,
  fmt,
  LoadingPanel,
} from "@/components/DashboardShell";
import { useTradeData } from "@/components/DataProvider";
import { perspectiveSnapshot } from "@/lib/analytics";
import { leadDistance, leadOrigin, leadRca, perspectiveConfig } from "@/lib/perspective";

export const Route = createFileRoute("/logistics")({
  head: () => ({ meta: [{ title: "Logistics — Ready4Trade" }] }),
  component: LogisticsPage,
});

function LogisticsPage() {
  const { leads, aggregates, isLoading, perspective } = useTradeData();
  const config = perspectiveConfig(perspective);
  const snapshot = useMemo(
    () => (leads ? perspectiveSnapshot(leads, perspective) : null),
    [leads, perspective],
  );
  const nearestMarkets = useMemo(() => {
    if (!leads) return [];
    const map = new Map<
      string,
      { total: number; count: number; aligned: number; origins: Map<string, number> }
    >();
    for (const lead of leads) {
      const distance = leadDistance(lead, perspective);
      if (distance == null || !lead.dc || lead.dc === "Unknown") continue;
      const row = map.get(lead.dc) ?? { total: 0, count: 0, aligned: 0, origins: new Map() };
      row.total += distance;
      row.count += 1;
      if ((leadRca(lead, perspective) ?? 0) > 1) row.aligned += 1;
      const origin = leadOrigin(lead, perspective) || config.origins;
      row.origins.set(origin, (row.origins.get(origin) || 0) + 1);
      map.set(lead.dc, row);
    }
    return [...map]
      .map(([name, row]) => ({
        name,
        distance: Math.round(row.total / row.count),
        leads: row.count,
        aligned: row.aligned,
        origin: [...row.origins].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—",
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 15);
  }, [config.origins, leads, perspective]);

  if (isLoading || !leads || !aggregates || !snapshot)
    return (
      <DashboardShell>
        <LoadingPanel />
      </DashboardShell>
    );

  return (
    <DashboardShell meta={aggregates.meta}>
      <PageHeading
        title={`${config.flag} Logistics intelligence`}
        subtitle={`Distances use the nearest available origin among ${config.origins}. They are straight-line screening distances, not road, rail or maritime routes.`}
        badge={`${fmt(snapshot.avgDistance)} km average`}
      />

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <ChartCard
          title="Distance distribution"
          subtitle={`Nearest-origin distance from ${config.country}`}
          icon={<Ruler className="w-4 h-4" />}
        >
          <ResponsiveContainer width="100%" height={390}>
            <BarChart data={snapshot.distances}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
              <XAxis
                dataKey="bucket"
                tick={{ fontSize: 10, fill: "#64748b" }}
                stroke="#cbd5e1"
                angle={-18}
                textAnchor="end"
                height={75}
              />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" radius={[7, 7, 0, 0]}>
                {snapshot.distances.map((_, index) => (
                  <Cell
                    key={index}
                    fill={index < 2 ? "#10b981" : index < 4 ? "#2563eb" : "#8b5cf6"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Lead flow by month"
          subtitle="Useful for spotting demand changes and scraping coverage"
          icon={<TrendingUp className="w-4 h-4" />}
        >
          <ResponsiveContainer width="100%" height={390}>
            <LineChart data={snapshot.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} stroke="#cbd5e1" />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="count" stroke="#2563eb" strokeWidth={3} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <ChartCard
          title="Nearest destination markets"
          subtitle="Average straight-line distance and RCA-aligned lead volume"
          icon={<MapPinned className="w-4 h-4" />}
        >
          <div className="overflow-auto max-h-[470px] rounded-xl border border-border">
            <table className="w-full text-xs">
              <thead className="bg-secondary text-muted-foreground sticky top-0">
                <tr>
                  <Th>Market</Th>
                  <Th>Likely origin</Th>
                  <Th className="text-right">Avg km</Th>
                  <Th className="text-right">Leads</Th>
                  <Th className="text-right">RCA &gt; 1</Th>
                </tr>
              </thead>
              <tbody>
                {nearestMarkets.map((row) => (
                  <tr key={row.name} className="border-t border-border hover:bg-secondary/40">
                    <td className="px-3 py-2 font-medium">{row.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{row.origin}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(row.distance)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(row.leads)}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">
                      {fmt(row.aligned)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>

        <ChartCard
          title="Shipping terms for RCA-aligned leads"
          subtitle="Commercial delivery responsibilities in the selected opportunity set"
          icon={<Ship className="w-4 h-4" />}
        >
          <ResponsiveContainer width="100%" height={430}>
            <BarChart
              data={snapshot.shippingAligned}
              layout="vertical"
              margin={{ left: 10, right: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
              <YAxis
                dataKey="name"
                type="category"
                width={170}
                tick={{ fontSize: 10, fill: "#334155" }}
                stroke="#cbd5e1"
              />
              <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#06b6d4" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      <section className="surface-panel p-5 text-sm text-muted-foreground">
        <strong className="text-foreground">Important:</strong> A low geographic distance does not
        guarantee a cheaper route. Validate border crossings, transit countries, sanctions, rail
        gauge, port access, shipment size, Incoterms and live freight quotations.
      </section>
    </DashboardShell>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-3 py-2 text-[10px] uppercase font-semibold tracking-wider ${className}`}>
      {children}
    </th>
  );
}
